import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, QMark } from "@/components/Logo";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/journey", label: "Get Started" },
  { to: "/knowledge", label: "Knowledge" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <span className="group-hover:scale-[1.02] transition-transform">
            <Logo light={!scrolled} />
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === n.to
                  ? (scrolled ? "text-emerald-700 bg-emerald-50" : "text-emerald-300 bg-white/10")
                  : (scrolled ? "text-slate-600 hover:text-[#0B3D2E] hover:bg-slate-100" : "text-white/85 hover:text-white hover:bg-white/10")
              }`}
            >
              {n.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:block">
          <Link to="/booking">
            <Button data-testid="nav-book-btn" className={`rounded-full px-5 font-semibold transition-colors ${scrolled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-[#0B3D2E]"}`}>
              Book Free Consultation
            </Button>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden grid place-items-center h-10 w-10 rounded-full transition-colors ${scrolled ? "text-[#0B3D2E] hover:bg-slate-100" : "text-white hover:bg-white/10"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 font-medium">
              {n.label}
            </Link>
          ))}
          <Link to="/booking" className="block pt-2">
            <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Book Free Consultation</Button>
          </Link>
        </div>
      )}
    </header>
  );
};
